import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  APPROVAL_MODE_LABELS,
  ApprovalMode,
  vehicleGroupSchema,
  type VehicleGroupData,
  type VehicleGroupInput,
} from '@fleet/shared';
import { vehicleGroupsApi, type VehicleGroup } from '../../api/vehicle-groups';
import { Modal } from '../../ui/Modal';
import { Field, inputClass } from '../../ui/Field';
import { Button } from '../../ui/Button';
import { ErrorNotice } from '../../ui/ErrorNotice';

export function VehicleGroupForm({
  group,
  onClose,
}: {
  /** Fehlt beim Anlegen. */
  group?: VehicleGroup;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    // Drei Generics, weil Eingabe und Ausgabe des Schemas nicht dasselbe sind:
    // Im Formular dürfen Felder mit Default fehlen, nach der Prüfung sind sie
    // gesetzt. `handleSubmit` liefert deshalb den Ausgabetyp.
  } = useForm<VehicleGroupInput, unknown, VehicleGroupData>({
    resolver: zodResolver(vehicleGroupSchema),
    defaultValues: group
      ? {
          name: group.name,
          description: group.description ?? '',
          approvalMode: group.approvalMode,
          colorHex: group.colorHex,
          isActive: group.isActive,
        }
      : { approvalMode: ApprovalMode.ALL, colorHex: '#3b82f6', isActive: true },
  });

  const colorHex = watch('colorHex');

  const save = useMutation({
    mutationFn: async (values: VehicleGroupData) => {
      if (group) await vehicleGroupsApi.update(group.id, values);
      else await vehicleGroupsApi.create(values);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['vehicle-groups'] });
      // Die Gruppenfarbe schlägt auf Fahrzeuge und Kalender durch.
      await queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      onClose();
    },
  });

  return (
    <Modal
      title={group ? 'Fahrzeuggruppe bearbeiten' : 'Neue Fahrzeuggruppe'}
      onClose={onClose}
    >
      <form
        onSubmit={(event) => {
          void handleSubmit((values) => save.mutateAsync(values))(event);
        }}
        className="space-y-4"
      >
        <Field label="Name" error={errors.name?.message}>
          <input {...register('name')} className={inputClass} autoFocus />
        </Field>

        <Field label="Beschreibung" optional error={errors.description?.message}>
          <input {...register('description')} className={inputClass} />
        </Field>

        <Field
          label="Freigabe"
          hint="Wird beim Anlegen einer Buchung festgeschrieben — eine spätere Änderung deutet laufende Buchungen nicht um."
          error={errors.approvalMode?.message}
        >
          <select {...register('approvalMode')} className={inputClass}>
            {Object.values(ApprovalMode).map((mode) => (
              <option key={mode} value={mode}>
                {APPROVAL_MODE_LABELS[mode]}
              </option>
            ))}
          </select>
        </Field>

        <Field
          label="Farbe"
          hint="Gilt im Kalender für alle Fahrzeuge der Gruppe, sofern sie keine eigene Farbe haben."
          error={errors.colorHex?.message}
        >
          {/*
            Nur das Textfeld ist registriert. Dasselbe Feld ein zweites Mal zu
            registrieren wäre der naheliegende, aber falsche Weg: React Hook Form
            hält je Feldnamen eine Referenz, die zweite Registrierung überschreibt
            die erste, und danach zeigt eines der beiden Felder dauerhaft einen
            veralteten Wert an.

            Der Farbwähler wird deshalb gesteuert geführt und schreibt über
            `setValue` zurück — das aktualisiert auch das registrierte Textfeld.
          */}
          <div className="flex items-center gap-2">
            <input
              type="color"
              value={/^#[0-9a-fA-F]{6}$/.test(colorHex ?? '') ? colorHex : '#3b82f6'}
              onChange={(event) =>
                setValue('colorHex', event.target.value, {
                  shouldDirty: true,
                  shouldValidate: true,
                })
              }
              className="h-9 w-14 shrink-0 rounded border border-slate-300"
              aria-label="Farbe wählen"
            />
            <input {...register('colorHex')} className={inputClass} />
          </div>
        </Field>

        <label className="flex items-center gap-2 text-sm">
          <input {...register('isActive')} type="checkbox" className="size-4" />
          Aktiv
        </label>

        {save.isError && <ErrorNotice error={save.error} />}

        <div className="flex justify-end gap-2 pt-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
